import { useEffect, useRef, useState } from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { MoonIcon, SunIcon } from 'lucide-react'
import {
  Skeleton,
  ToggleButton,
  spotlightRingCss,
  SPOTLIGHT_PREVIEW_MIN_RADIUS,
} from '@repo/ui'
import { ITheme } from 'store/theme/types'
import { useEmailPreview } from 'hooks/api/useEmailStudio'
import { vhMinus } from 'utils/textZoom'
import { EmailSampleData } from './EmailSampleData'
import { TOKEN_IMPACT, type StudioTokenName } from './tokenImpact'

interface EmailPreviewProps {
  formValues: Partial<ITheme>
  /**
   * VRG-549: the Studio token whose parts of the email should be outlined, or null.
   *
   * The email needs no bridge — `srcDoc` is same-origin, which is how `applyAppearance`
   * below already reaches into the document. It also needs no new markup: `base-email`
   * emits `.v-btn` / `.v-text` / `.v-border` / `.v-bg` for its own dark-mode block, and
   * that turns out to be an exact token attribution map. Nothing was added to the email
   * HTML for this, which matters — annotation markup would otherwise ship in every
   * message a brand's customers receive.
   */
  highlightToken?: StudioTokenName | null
  /**
   * What was actually outlined. Measured here, not asserted by the caller.
   *
   * `invisible` is always 0 on this surface and the shape is shared with the certificate
   * preview only so the caller has one handler. The email highlight resolves the `.v-*`
   * classes `base-email` already emits — a real attribution map rather than a
   * differential — and an email has no closed drawers to hide anything behind.
   */
  onHighlightResult?: (result: {
    parts: { label: string; count: number }[]
    invisible: number
  }) => void
  /** Selected email template id (from the nav dropdown). */
  templateId?: string
  /** Selected template's display name. */
  name?: string
  /** Recipient-facing subject line. */
  subject?: string
  /** One-line "sent when…" trigger description. */
  description?: string
  /** Company logo URL injected into the email header/footer. */
  logo?: string
  /** The selected template's demo content, for the sample-data panel (VRG-560). */
  sampleProps?: Record<string, any>
}

/**
 * Icon-only: the pane header is narrow and "Light"/"Dark" as text beside a title and a
 * subject line is three competing blocks. A sun and a moon carry it with no reading at all,
 * so the labels are dropped and `ariaLabel` supplies the accessible name.
 */
const APPEARANCE_OPTIONS = [
  { label: '', ariaLabel: 'Light', value: 'light', icon: <SunIcon size={15} strokeWidth={1.5} /> },
  { label: '', ariaLabel: 'Dark', value: 'dark', icon: <MoonIcon size={15} strokeWidth={1.5} /> },
]

/**
 * Switch the email between its OWN light and dark renderings.
 *
 * NOT a CSS filter, which is what this was first built as and why it produced nonsense.
 * The email already ships BOTH appearances: inline styles carry the light values and a
 * `@media (prefers-color-scheme: dark)` block overrides them with the dark ones, including
 * which logo variant is displayed. A filter sits OUTSIDE all that and repaints whatever the
 * media query already chose — so the document picked a white logo for a dark card (correct),
 * the filter then lightened the card underneath it, and the pairing came apart. Two
 * mechanisms deciding the same thing.
 *
 * So instead of simulating, this selects. Rewriting the media CONDITION inside the loaded
 * document forces one branch or the other:
 *
 *   dark  -> `@media all`      the dark rules always apply
 *   light -> `@media not all`  they never do, leaving the inline light values
 *
 * Three things follow from using the email's own CSS rather than a transform. It is EXACT,
 * not an approximation, so there is no caveat to write. The logo, card, borders, text and
 * button all move together because they are all in that one block. And no token value is
 * duplicated in the platform — `@repo/emails` is not even a dependency here, so a filter or
 * a hardcoded palette were the only alternatives, and both would drift the moment the email's
 * dark tokens changed.
 *
 * Same-origin srcDoc, so reaching into the document is available. Stores the original text on
 * the element so toggling back is exact rather than reconstructed.
 */
const applyAppearance = (doc: Document | null | undefined, dark: boolean): void => {
  if (!doc) return
  const styles = Array.from(doc.querySelectorAll('style'))
  for (const el of styles) {
    const original = el.dataset.veraOriginal ?? el.textContent ?? ''
    if (!original.includes('prefers-color-scheme')) continue
    if (!el.dataset.veraOriginal) el.dataset.veraOriginal = original
    el.textContent = original.replace(
      /@media\s*\(prefers-color-scheme:\s*dark\)/g,
      dark ? '@media all' : '@media not all',
    )
  }
}

const HIGHLIGHT_STYLE_ID = 'vera-token-highlight'

/**
 * Outline the parts of the rendered email a Studio token governs, and report what was
 * matched.
 *
 * Injected as ONE stylesheet whose selectors are the token's parts, rather than a class
 * per element — same reasoning as the app half: the document is replaced wholesale on
 * every re-render of the preview, so marking nodes would mean re-marking them on every
 * keystroke, while a stylesheet is re-injected once per document and is otherwise inert.
 *
 * The treatment is the PRODUCT TOUR's spotlight ring, from `@repo/ui` — one "look at this"
 * in the product, not two (Spencer: *"just want to use the same tour spotlight — not the
 * dotted or hashed line spotlights"*). It was a dashed indigo of this feature's own
 * invention until then.
 *
 * The values are static and deliberately NOT derived from any theme: the mark exists to
 * show what a brand colour is about to change, so drawing it in that colour would hide it
 * on exactly the brands that need it most. `contrastHairline` keeps it legible against the
 * email's dark appearance, which the tour does not need because it dims its page.
 */
const EMAIL_HIGHLIGHT_RING = spotlightRingCss({
  offset: 2,
  contrastHairline: true,
  important: true,
})

/**
 * Marks the elements whose corners the ring is allowed to soften. See
 * `SPOTLIGHT_PREVIEW_MIN_RADIUS` — an `outline` follows the element's OWN corners and
 * `outline-radius` does not exist, so the only way to round a ring is to round the thing
 * it rings, which is why this is the consumer's job and why it is bounded.
 */
const HIGHLIGHT_ROUND_ATTR = 'data-vera-highlight-round'

/**
 * The four longhands, read rather than the `border-radius` shorthand: `getComputedStyle`
 * resolves the shorthand to a string that has to be re-parsed, while the longhands each
 * return one resolved value. A percentage (`50%` on a circular avatar) parses non-zero and
 * is therefore excluded, which is the correct answer.
 */
const RADIUS_LONGHANDS = [
  'border-top-left-radius',
  'border-top-right-radius',
  'border-bottom-right-radius',
  'border-bottom-left-radius',
] as const

/**
 * Is this element's radius already 0 on all four corners?
 *
 * Computed style must come from the IFRAME's own view, not the platform's `window` — the
 * elements belong to another document, and `window.getComputedStyle` on a foreign node is
 * not what it looks like.
 */
const isAlreadySquare = (el: Element, view: Window): boolean => {
  const c = view.getComputedStyle(el)
  return RADIUS_LONGHANDS.every((p) => parseFloat(c.getPropertyValue(p) || '0') === 0)
}

/**
 * The full-bleed wall the message sits on — `base-email`'s outermost `.v-bg` Section.
 *
 * Excluded from the corner floor for the same reason the app half excludes its page
 * surface: the wall fills the frame, so rounding it pulls its corners away from the
 * preview's own edge and leaves four notches of the frame's background showing. That reads
 * as a restyle of the thing being judged rather than as a softened ring. Identified by
 * being the OUTERMOST `.v-bg` — `EmailCode` also carries `v-bg`, sits inside the wall, and
 * is already rounded anyway.
 */
const isEmailWall = (el: Element): boolean =>
  el.classList.contains('v-bg') && !el.parentElement?.closest('.v-bg')

const applyEmailHighlight = (
  doc: Document | null | undefined,
  token: StudioTokenName | null,
): { label: string; count: number }[] => {
  if (!doc) return []
  doc.getElementById(HIGHLIGHT_STYLE_ID)?.remove()
  // Before any measuring: a mark left from the previous token would make its element
  // compute 4px and fail the already-square test, so the floor would be applied once and
  // then never re-applied for the rest of the document's life.
  doc.querySelectorAll(`[${HIGHLIGHT_ROUND_ATTR}]`).forEach((el) => {
    el.removeAttribute(HIGHLIGHT_ROUND_ATTR)
  })
  if (!token) return []

  const parts = TOKEN_IMPACT[token]?.emailParts ?? []
  const matched: { label: string; count: number }[] = []
  const selectors: string[] = []

  for (const part of parts) {
    let count = 0
    try {
      count = doc.querySelectorAll(part.selector).length
    } catch {
      count = 0
    }
    // Zero is dropped rather than shown: it means "not in this template", which the
    // panel already says in general terms, and a row of zeroes reads as a broken feature.
    if (count > 0) {
      matched.push({ label: part.label, count })
      selectors.push(part.selector)
    }
  }

  if (selectors.length) {
    const joined = selectors.join(', ')
    const rules = [`${joined} {\n  ${EMAIL_HIGHLIGHT_RING}\n}`]

    /*
      The corner floor. Applied per element rather than as a flat rule on the ring's own
      selector, because `SPOTLIGHT_PREVIEW_MIN_RADIUS` may only ever RAISE a radius: a blanket
      `border-radius: 4px` here would square off the message card (12px) and the button
      (the brand's own radius — 10px on the company this was measured against), which is a
      larger lie than the hard-cornered ring it sets out to fix.

      Suppressed entirely for `border_radius`, the token whose control IS corner radius —
      rounding corners to annotate that control would put the annotation in contradiction
      with the thing it annotates. That is not a hypothetical: `border_radius`'s only email
      part is the button, so an operator who sets the radius to 0 would otherwise watch the
      button they just squared off render with 4px corners, in the very act of asking what
      the control does.

      This mirrors `tokenHighlight.ts`'s `shouldRound` in the app preview rather than sharing
      it. That function is a closure over the differential measurement's own state — the
      changed-property map and the computed page surface — none of which exists on this side,
      where parts are resolved from `base-email`'s `.v-*` attribution classes. And
      `tokenHighlight.ts` deliberately imports nothing from `@repo/ui` (it runs inside the
      app's shadow root, and the caller passes the values in), so a shared helper could never
      have both consumers. What IS shared, and is the thing that would otherwise drift, is the
      radius itself: `SPOTLIGHT_PREVIEW_MIN_RADIUS`, imported by both.
    */
    const view = doc.defaultView
    if (view && token !== 'border_radius') {
      let rounded = 0
      doc.querySelectorAll(joined).forEach((el) => {
        if (el === doc.body || el === doc.documentElement) return
        if (isEmailWall(el)) return
        if (!isAlreadySquare(el, view)) return
        el.setAttribute(HIGHLIGHT_ROUND_ATTR, '')
        rounded += 1
      })
      if (rounded > 0) {
        rules.push(
          `[${HIGHLIGHT_ROUND_ATTR}] {\n  border-radius: ${SPOTLIGHT_PREVIEW_MIN_RADIUS}px !important;\n}`,
        )
      }
    }

    const style = doc.createElement('style')
    style.id = HIGHLIGHT_STYLE_ID
    style.textContent = rules.join('\n')
    ;(doc.head ?? doc.body)?.appendChild(style)
  }

  return matched
}

/**
 * Live preview of an email template with the company's (unsaved) Studio theme +
 * logo applied, server-rendered via the company-scoped preview endpoint
 * (VERA-270). Styles are debounced so dragging a color picker doesn't fire a
 * request per tick.
 *
 * The email renders at its natural width (the email's own 600px container is
 * centered by the template); the iframe fills the pane so the email's
 * background "wall" shows around the card — like an email client.
 *
 * Double-buffered: the newly-rendered HTML loads into a hidden back-buffer
 * iframe and only crossfades into view once it's fully painted, so editing the
 * theme transitions smoothly instead of flashing the whole email white on every
 * keystroke (an iframe blanks to white while it reloads its document).
 */
export const EmailPreview = ({
  formValues,
  templateId,
  name,
  subject,
  description,
  logo,
  sampleProps,
  highlightToken = null,
  onHighlightResult,
}: EmailPreviewProps) => {
  const theme = useTheme()
  const [darkMode, setDarkMode] = useState(false)
  const [debouncedStyles, setDebouncedStyles] = useState<Partial<ITheme>>(formValues)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedStyles(formValues), 400)
    return () => clearTimeout(t)
  }, [formValues])

  /**
   * VRG-560 — the sample-data panel's props, or `undefined` when the template
   * needs none.
   *
   * Plain component state, deliberately NOT a react-hook-form field: that is what
   * makes picking a sample structurally unable to dirty Studio's Save (§54). See
   * `EmailSampleData`'s docblock.
   *
   * Resetting this on a template change is `EmailSampleData`'s job, not this
   * component's — it holds the panel's other state (the product pick) and the
   * sample owner it has to re-seed, so splitting the reset across both meant two
   * effects racing to write this one value in a single commit. `templateId` is
   * passed down as that reset key.
   */
  const [sampleOverrides, setSampleOverrides] = useState<Record<string, any> | undefined>(undefined)

  const { data: html, isFetching, isError } = useEmailPreview({
    templateId,
    styles: debouncedStyles,
    logo,
    props: sampleOverrides,
  })

  // Two stacked iframes (A/B). One is in front (visible); when new HTML arrives
  // we load it into the back buffer and only swap once it's painted.
  const [bufferA, setBufferA] = useState<string | undefined>(undefined)
  const [bufferB, setBufferB] = useState<string | undefined>(undefined)
  const [frontIsA, setFrontIsA] = useState(true)
  const loadingBuf = useRef<'A' | 'B' | null>(null)
  const refA = useRef<HTMLIFrameElement>(null)
  const refB = useRef<HTMLIFrameElement>(null)
  const [frameHeight, setFrameHeight] = useState(700)

  const measure = (ref: React.RefObject<HTMLIFrameElement>) => {
    try {
      // BEFORE measuring: the two appearances can differ in height, so measuring the
      // light one and then switching leaves the frame the wrong size.
      applyAppearance(ref.current?.contentDocument, darkMode)
      const h = ref.current?.contentDocument?.body?.scrollHeight
      if (h) setFrameHeight(h)
    } catch {
      /* same-origin srcDoc, so this won't normally fire */
    }
  }

  // When new HTML arrives, load it into whichever buffer is currently behind.
  useEffect(() => {
    if (!html) return
    const front = frontIsA ? bufferA : bufferB
    if (html === front) return
    if (frontIsA) {
      setBufferB(html)
      loadingBuf.current = 'B'
    } else {
      setBufferA(html)
      loadingBuf.current = 'A'
    }
  }, [html]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoad = (buf: 'A' | 'B', ref: React.RefObject<HTMLIFrameElement>) => () => {
    if (loadingBuf.current !== buf) return
    measure(ref)
    setFrontIsA(buf === 'A')
    loadingBuf.current = null
  }

  // Re-apply whenever the toggle flips or a new buffer paints. Both iframes are touched:
  // the back buffer is mid-load and would otherwise crossfade in wearing the old appearance.
  useEffect(() => {
    applyAppearance(refA.current?.contentDocument, darkMode)
    applyAppearance(refB.current?.contentDocument, darkMode)
  }, [darkMode, bufferA, bufferB, frontIsA])

  // VRG-549: the same treatment for the token highlight, and for the same reason — a new
  // document arrives on every theme edit, so the outline has to be re-injected into
  // BOTH buffers or it disappears the moment the operator moves a picker. Only the FRONT
  // buffer's count is reported; the back one is mid-load and would report a stale tree.
  const onHighlightResultRef = useRef(onHighlightResult)
  onHighlightResultRef.current = onHighlightResult
  useEffect(() => {
    const front = frontIsA ? refA : refB
    const back = frontIsA ? refB : refA
    applyEmailHighlight(back.current?.contentDocument, highlightToken)
    const parts = applyEmailHighlight(front.current?.contentDocument, highlightToken)
    onHighlightResultRef.current?.({ parts, invisible: 0 })
  }, [highlightToken, bufferA, bufferB, frontIsA])

  const showSkeleton = isFetching && !html && !bufferA && !bufferB
  const iframeBaseStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
    transition: 'opacity 220ms ease',
  }

  return (
    <Box
      sx={{
        flex: 1,
        border: `1px solid ${theme.palette.grey[100]}`,
        borderRadius: 3,
        p: 3,
        // `vh - 186.5`, matching Preview's STUDIO_PANE_OFFSET: from under the tab row to
        // the page's 24px bottom gutter, less the 16px of air above the pane. The .5 stops
        // the pane overhanging its container by 1.5px and twitching at max scroll — see
        // the note on STUDIO_PANE_OFFSET.
        // These numbers are duplicated from Preview.tsx and MUST move together — the two
        // panes sit in the same slot, so a mismatch shows as the Email tab's preview sitting
        // 16px off from the Certificate tab's.
        minHeight: vhMinus(186.5),
        maxHeight: vhMinus(186.5),
        // The wall behind the email follows the toggle too. A dark-inverted message
        // sitting on a light backdrop reads as a rendering fault rather than as a
        // dark-mode inbox, and the whole point is to judge the email in context.
        backgroundColor: darkMode ? theme.palette.common.black : theme.palette.grey[50],
        overflowY: 'auto',
        position: 'sticky',
        // 138.5, matching Preview's STUDIO_PANE_STICKY_TOP. The `mt` below rests the pane
        // 16px under the chrome block and this pins it at the same place; pure-sticky is the
        // two agreeing.
        top: 138.5,
        mt: '16px',
      }}
    >
      {showSkeleton ? (
        <Skeleton variant="rounded" sx={{ width: '100%', height: 560 }} />
      ) : isError ? (
        <Typography variant="body3" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 10 }}>
          Couldn't render this email preview.
        </Typography>
      ) : !templateId ? (
        <Typography variant="body3" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 10 }}>
          Select an email template to preview it.
        </Typography>
      ) : (
        <Stack spacing={7}>
          {/* Header: what this email is, its subject, and when it's sent.
              Tight to the top, with a generous gap below before the preview. */}
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={4}
            sx={{ pt: 2 }}
          >
            <Stack spacing={2.5} sx={{ minWidth: 0 }}>
            <Stack spacing={1}>
              {name && (
                <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4 }}>
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    Title:{' '}
                  </Box>
                  {name}
                </Typography>
              )}
              {subject && (
                <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    Subject:{' '}
                  </Box>
                  {subject}
                </Typography>
              )}
            </Stack>
            {description && (
              <Typography sx={{ fontSize: 13, lineHeight: 1.5 }} color="text.secondary">
                {description}
              </Typography>
            )}
            {/* Says what it is. Clients each invert differently and back off when a
                message opts in, so presenting this as "how it looks in dark mode"
                would be a claim we cannot support. What it reliably catches is a
                logo or brand colour that goes illegible once a background flips. */}
            </Stack>
            <ToggleButton
              tabs={APPEARANCE_OPTIONS}
              tab={darkMode ? 'dark' : 'light'}
              onChange={(v) => setDarkMode(v === 'dark')}
              sx={{ flexShrink: 0 }}
            />
          </Stack>

          <Box
            sx={{
              position: 'relative',
              height: frameHeight,
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.grey[300]}`,
              boxShadow: theme.shadows[1],
              transition: 'height 220ms ease',
            }}
          >
            <iframe
              ref={refA}
              title="Email preview A"
              srcDoc={bufferA}
              onLoad={handleLoad('A', refA)}
              style={{ ...iframeBaseStyle, opacity: frontIsA ? 1 : 0 }}
            />
            <iframe
              ref={refB}
              title="Email preview B"
              srcDoc={bufferB}
              onLoad={handleLoad('B', refB)}
              style={{ ...iframeBaseStyle, opacity: frontIsA ? 0 : 1 }}
            />
          </Box>

          {/* The sample-data panel fills the space under the rendered email — the
              region that was previously empty scrollport. It sits HERE, inside the
              preview pane, rather than in the editor column, because that placement
              is what says "this belongs to the preview" before the copy has to. */}
          <EmailSampleData
            templateId={templateId}
            sampleProps={sampleProps}
            value={sampleOverrides}
            onChange={setSampleOverrides}
          />
        </Stack>
      )}
    </Box>
  )
}
